"use client";

import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import AssetParent from "./AssetParent";
import { Box } from "@thenounproject/lingo-core";
import useResizeObserver from "@components/hooks/useResizeObserver";

const VideoContainer = styled(Box).attrs({
  height: "100%",
  width: "100%",
})``;
type Props = {
  src: string;
  placeholder: string;
  dimensions: string;
};

const VideoPlayer: React.FC<Props> = ({ src, placeholder, dimensions }) => {
  const [sizeProps, setSizeProps] = useState({}),
    containerRef = useRef<HTMLDivElement>(),
    videoRef = useRef<HTMLVideoElement>(),
    [maxHeight, setMaxHeight] = useState(0),
    [maxWidth, setMaxWidth] = useState(0),
    playVideo = useCallback(() => {
      videoRef.current?.play().catch(() => {
        videoRef.current.muted = true;
        void videoRef.current.play();
      });
    }, []);

  const [width, height] = dimensions.split("x").map(d => parseInt(d));

  useLayoutEffect(() => {
    if (width <= maxWidth) {
      if (height <= maxHeight) {
        setSizeProps({});
      } else {
        setSizeProps({ height: `${maxHeight}px` });
      }
    } else if (height <= maxHeight) {
      setSizeProps({ width: `${maxWidth}px` });
    } else {
      setSizeProps(
        maxWidth / width > maxHeight / height
          ? {
              height: `${maxHeight}px`,
            }
          : {
              width: `${maxWidth}px`,
            }
      );
    }
  }, [height, maxHeight, maxWidth, width]);

  const onContainerResize = useCallback(() => {
    const node = containerRef.current;
    if (!node) return;
    setMaxHeight(node.clientHeight);
    setMaxWidth(node.clientWidth);
  }, []);

  useResizeObserver(containerRef, onContainerResize);

  if (!src) return null;

  const extension = src.split(/[#?]/)[0].split(".").slice(-1)[0].replace("mov", "mp4");
  return (
    <VideoContainer ref={containerRef}>
      <AssetParent square={false} className="parent asset-parent">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          {...sizeProps}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translateX(-50%) translateY(-50%)",
          }}
          poster={placeholder}
          controls
          autoPlay
          onCanPlay={playVideo}>
          <source src={src} type={`video/${extension}`} />
          <p>Sorry, this video can&apos;t be played in this browser.</p>
        </video>
      </AssetParent>
    </VideoContainer>
  );
};

export default VideoPlayer;
